import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';
import { featuredProducts, productCategories } from '../../mocks/products';
import { useCart } from '../../hooks/useCart';

export default function ProductsPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('featured');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 18;
  const { addToCart } = useCart();
  
  // Load products from localStorage or use default
  const [allProducts, setAllProducts] = useState(() => {
    const savedProducts = localStorage.getItem('adminProducts');
    return savedProducts ? JSON.parse(savedProducts) : featuredProducts;
  });

  // Track image loading errors
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});

  const handleImageError = (productId: number, category: string) => {
    setImageErrors(prev => ({ ...prev, [productId]: true }));
  };

  const getPlaceholderImage = (category: string) => {
    const placeholders: Record<string, string> = {
      'eBooks': 'https://public.readdy.ai/ai/img_res/2fe25104f583ac010e424426db0750d2.jpg',
      'Image Libraries': 'https://readdy.ai/api/search-image?query=professional%20stock%20photo%20collection%20showcase%2C%20grid%20of%20high%20quality%20images%2C%20photography%20portfolio%20display%2C%20clean%20modern%20presentation&width=400&height=300&seq=placeholder-images&orientation=landscape',
      'Mini Videos': 'https://readdy.ai/api/search-image?query=professional%20video%20template%20showcase%2C%20mobile%20phone%20displaying%20video%20content%2C%20modern%20video%20marketing%20materials%2C%20clean%20presentation&width=400&height=300&seq=placeholder-video&orientation=landscape'
    };
    return placeholders[category] || placeholders['eBooks'];
  };

  // Update products when localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const savedProducts = localStorage.getItem('adminProducts');
      if (savedProducts) {
        setAllProducts(JSON.parse(savedProducts));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for changes
    const interval = setInterval(() => {
      const savedProducts = localStorage.getItem('adminProducts');
      if (savedProducts) {
        const parsed = JSON.parse(savedProducts);
        if (JSON.stringify(parsed) !== JSON.stringify(allProducts)) {
          setAllProducts(parsed);
          setCurrentPage(1); // Reset to first page when products change
        }
      }
    }, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [allProducts]);

  const filteredProducts = allProducts.filter(product => {
    const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low': return a.price - b.price;
      case 'price-high': return b.price - a.price;
      case 'rating': return b.rating - a.rating;
      case 'downloads': return b.downloadCount - a.downloadCount;
      default: return 0;
    }
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const currentProducts = sortedProducts.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, sortBy, searchQuery]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    if (currentPage > 1) {
      pages.push(
        <button
          key="prev"
          onClick={() => handlePageChange(currentPage - 1)}
          className="px-3 py-2 text-gray-600 hover:text-blue-600 cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
        </button>
      );
    }

    // First page
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="px-3 py-2 text-gray-600 hover:text-blue-600 cursor-pointer"
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="dots1" className="px-3 py-2 text-gray-400">...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-2 cursor-pointer ${
            currentPage === i
              ? 'bg-blue-600 text-white rounded-lg'
              : 'text-gray-600 hover:text-blue-600'
          }`}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="dots2" className="px-3 py-2 text-gray-400">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="px-3 py-2 text-gray-600 hover:text-blue-600 cursor-pointer"
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    if (currentPage < totalPages) {
      pages.push(
        <button
          key="next"
          onClick={() => handlePageChange(currentPage + 1)}
          className="px-3 py-2 text-gray-600 hover:text-blue-600 cursor-pointer"
        >
          <i className="ri-arrow-right-line"></i>
        </button>
      );
    }

    return (
      <div className="flex items-center justify-center space-x-1 mt-8">
        {pages}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Page Header */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">Digital Products</h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Discover our comprehensive collection of premium digital products designed to transform your business operations and accelerate growth.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <div className="lg:w-1/4">
            <Card className="p-6 sticky top-24">
              <h3 className="text-lg font-semibold mb-4">Filters</h3>
              
              {/* Search */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Products</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <i className="ri-search-line absolute left-3 top-2.5 text-gray-400 text-sm"></i>
                </div>
              </div>

              {/* Categories */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Categories</label>
                <div className="space-y-2">
                  <button
                    onClick={() => setSelectedCategory('All')}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedCategory === 'All' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    All Products
                  </button>
                  {productCategories.map((category) => (
                    <button
                      key={category.name}
                      onClick={() => setSelectedCategory(category.name)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${
                        selectedCategory === category.name ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span>{category.name}</span>
                      <span className="text-xs">({category.count})</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="featured">Featured</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="rating">Highest Rated</option>
                  <option value="downloads">Most Downloaded</option>
                </select>
              </div>
            </Card>
          </div>

          {/* Products Grid */}
          <div className="lg:w-3/4">
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-600">
                Showing {startIndex + 1}-{Math.min(endIndex, sortedProducts.length)} of {sortedProducts.length} products
                {currentPage > 1 && ` (Page ${currentPage} of ${totalPages})`}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-product-shop>
              {currentProducts.map((product) => (
                <Card key={product.id} hover className="overflow-hidden">
                  <div className="relative w-full h-48 bg-gray-100">
                    <img 
                      src={imageErrors[product.id] ? getPlaceholderImage(product.category) : product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top"
                      onError={() => handleImageError(product.id, product.category)}
                      loading="lazy"
                    />
                    {product.originalPrice > product.price && (
                      <div className="absolute top-4 right-4">
                        <span className="bg-red-500 text-white px-2 py-1 rounded-full text-sm font-semibold">
                          Save ${(product.originalPrice - product.price).toFixed(0)}
                        </span>
                      </div>
                    )}
                    {(product as any).isCustomProduct && (
                      <div className="absolute top-4 left-4">
                        <span className="bg-green-500 text-white px-2 py-1 rounded-full text-sm font-semibold">
                          New
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-blue-600 font-medium">{product.category}</span>
                      <div className="flex items-center space-x-1">
                        <i className="ri-star-fill text-yellow-400 text-sm"></i>
                        <span className="text-sm text-gray-600">{product.rating}</span>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{product.description}</p>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl font-bold text-gray-900">${product.price}</span>
                        {product.originalPrice > product.price && (
                          <span className="text-lg text-gray-500 line-through">${product.originalPrice}</span>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">{product.downloadCount} downloads</span>
                    </div>
                    
                    {(product as any).fileName && (
                      <div className="mb-4 p-2 bg-gray-50 rounded text-xs">
                        <div className="flex items-center space-x-2">
                          <i className="ri-file-line text-gray-500"></i>
                          <span className="text-gray-700 truncate">{(product as any).fileName}</span>
                        </div>
                        {(product as any).fileSize && (
                          <span className="text-gray-500">{(product as any).fileSize}</span>
                        )}
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => addToCart({
                          id: product.id,
                          name: product.name,
                          price: product.price,
                          image: product.image,
                          category: product.category
                        })}
                        className="whitespace-nowrap cursor-pointer font-medium transition-all duration-200 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl px-3 py-1.5 text-sm rounded-md flex-1"
                      >
                        <i className="ri-shopping-cart-line"></i>
                        Add to Cart
                      </button>
                      <Link to={`/product-detail/${product.id}`}>
                        <Button variant="outline" size="sm">
                          <i className="ri-eye-line"></i>
                          View
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {currentProducts.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-search-line text-4xl text-gray-400 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-600">Try adjusting your search or filter criteria</p>
              </div>
            )}

            {/* Pagination */}
            {renderPagination()}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}